/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { MapData, MapEdgeType, MapNodeType } from "client";
import { Const } from "const";

/**
 * Convert geolocation to LatLngLiteral.
 * @param {number[]} geolocation [longitude, latitude]
 * @return {LatLngLiteral|undefined}
 */
export function convertLatLngFromGeolocation(
  geolocation?: number[]
): google.maps.LatLngLiteral | undefined {
  if (!geolocation) {
    return undefined;
  }
  return {
    lat: geolocation[1],
    lng: geolocation[0],
  };
}

/**
 * Convert bidirectional edges to one direction.
 * @param {(MapEdgeType|undefined)[]} edges
 * @return {MapEdgeType[]}
 */
export function removeDuplicateEdges(
  edges: (MapEdgeType | undefined)[]
): MapEdgeType[] {
  return Array.from(
    edges
      ?.reduce<Map<string, MapEdgeType>>((acc, edge) => {
        if (edge) {
          // one direction edge
          const key = [edge.startNodeId, edge.endNodeId].sort().join("-");
          // If edge is roadclosure, replace other one.
          if (edge.priority === 1 || !acc.has(key)) {
            acc.set(key, edge);
          }
        }
        return acc;
      }, new Map())
      ?.values()
  );
}

/**
 * filter Closed Edges
 * @param {(MapEdgeType|undefined)[]} inputEdges
 * @return {MapEdgeType[]}
 */
export function filterClosedEdges(
  inputEdges: (MapEdgeType | undefined)[]
): MapEdgeType[] {
  const filteredEdges: MapEdgeType[] = [];
  if (inputEdges.some((edge) => typeof edge === "undefined")) {
    return filteredEdges;
  }
  const groupedEdges: Map<string, MapEdgeType[]> = new Map();
  inputEdges.forEach((edge) => {
    if (edge) {
      const key = [edge.startNodeId, edge.endNodeId].sort().join("-");
      if (!groupedEdges.has(key)) {
        groupedEdges.set(key, []);
      }
      groupedEdges.get(key)?.push(edge);
    }
  });

  groupedEdges.forEach((groupedEdge) => {
    const isEdgeClosed = groupedEdge.every((edge) => edge.priority === 0);

    if (isEdgeClosed) {
      // priorityが0のものを追加
      filteredEdges.push(groupedEdge[0]);
    }
  });

  return filteredEdges;
}

/**
 * filter Oneway Edges
 * @param {(MapEdgeType|undefined)[]} inputEdges
 * @return {MapEdgeType[]}
 */
export function filterOnewayEdges(
  inputEdges: (MapEdgeType | undefined)[]
): MapEdgeType[] {
  const filteredEdges: MapEdgeType[] = [];
  if (inputEdges.some((edge) => typeof edge === "undefined")) {
    return filteredEdges;
  }
  const groupedEdges: Map<string, MapEdgeType[]> = new Map();
  inputEdges.forEach((edge) => {
    if (edge) {
      const key = [edge?.startNodeId, edge?.endNodeId].sort().join("-");
      if (!groupedEdges.has(key)) {
        groupedEdges.set(key, []);
      }
      groupedEdges.get(key)?.push(edge);
    }
  });

  groupedEdges.forEach((groupedEdge) => {
    const isSomeEdgeOpened = groupedEdge.some((edge) => edge.priority === 1);
    const isSomeEdgeClosed = groupedEdge.some((edge) => edge.priority === 0);

    if (isSomeEdgeOpened && isSomeEdgeClosed) {
      const onewayClosedEdge = groupedEdge.find((edge) => edge.priority === 1);
      if (onewayClosedEdge) {
        filteredEdges.push(onewayClosedEdge);
      }
    }
  });

  return filteredEdges;
}

/**
 * Check if node is closed
 * @param {MapNodeType|MapEdgeType} element Node
 * @return {boolean} true: closed/false: not closed
 */
export function isClosed(element: MapNodeType | MapEdgeType) {
  if (getElementType(element) === "Node") {
    // In case node
    const node = element as MapNodeType;
    return node.priority === Const.priorityClosed;
  }
  // In edge node
  const edge = element as MapEdgeType;
  return edge.priority === Const.priorityClosed;
}

/**
 * Set node is closed or not
 * @param {MapNodeType|MapEdgeType} element Route element
 * @param {boolean} closed true: to closed / false: to not closed
 */
export function setClosed(element: MapNodeType | MapEdgeType, closed: boolean) {
  if (getElementType(element) === "Node") {
    // In case node
    const node = element as MapNodeType;
    node.priority = closed ? Const.priorityClosed : Const.priorityNotClosed;
  } else {
    // In edge node
    const edge = element as MapEdgeType;
    edge.priority = closed ? Const.priorityClosed : Const.priorityNotClosed;
  }
}

export type ElementType = "Edge" | "Node";
export interface RouteElementSummary {
  type: ElementType;
  id: number;
}

/**
 * Get type of route element
 * @param {MapNodeType | MapEdgeType} routeElement element
 * @return {ElementType} Type of element
 */
export function getElementType(
  routeElement: MapNodeType | MapEdgeType
): ElementType {
  if ("startNodeId" in routeElement) {
    return "Edge";
  }
  return "Node";
}

/**
 * Get route element from map
 * @param {MapData} map Map
 * @param {RouteElementSummary} elementSummary Summary of route element
 * @return {apNodeType | MapEdgeType | undefined} Route element(undefined if not found)
 */
export function findRouteElement(
  map: MapData,
  elementSummary: RouteElementSummary
): MapNodeType | MapEdgeType | undefined {
  if (elementSummary.type === "Edge") {
    return map.mapInfo
      .flatMap((m) => m.edges)
      .find((e) => e?.id === elementSummary.id);
  } else {
    return map.mapInfo
      .flatMap((m) => m.nodes)
      .find((n) => n?.id === elementSummary.id);
  }
}
